import {
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { getRuntimeAuthConfig } from 'core/auth/authConfig';
import { msalInstance } from 'index';

const AUTH_REDIRECT_IN_PROGRESS = 'AUTH_REDIRECT_IN_PROGRESS';
let authRedirectInProgress = false;

type RequestBody =
  | Record<string, unknown>
  | string
  | number
  | boolean
  | null
  | Array<unknown>;

type requestFunction = (
  endpoint: string,
  body?: RequestBody,
  options?: {
    noAuth?: boolean;
    method?: 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH';
    timeout?: number;
  }
) => Promise<Response>;

const request: requestFunction = async (endpoint, body, options) => {
  const fetchOptions: RequestInit & { headers: Record<string, string> } = {
    method: options?.method || 'GET',
    signal: options?.timeout ? AbortSignal.timeout(options.timeout) : undefined,
    headers: {
      Accept: 'application/json',
    },
  };
  if (body) {
    fetchOptions.body = JSON.stringify(body);
    fetchOptions.headers['Content-Type'] = 'application/json';
  }
  if (!options?.noAuth) {
    const token = await acquireAccessToken(msalInstance);
    fetchOptions.headers['Authorization'] = 'Bearer ' + token;
  }

  const response = await fetch(endpoint, fetchOptions);
  if (response.status === 401 && !options?.noAuth) {
    window.location.href = '/login?tokenExpired=true';
  }
  if (response.status === 403 && !options?.noAuth) {
    window.location.href = '/unauthorized';
  }
  return response;
};

const acquireAccessToken = async (msalInstance: PublicClientApplication) => {
  const activeAccount = msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
  const accounts = msalInstance.getAllAccounts();

  const startAuthRedirect = async (redirect: () => Promise<void>) => {
    if (authRedirectInProgress) {
      throw new Error(AUTH_REDIRECT_IN_PROGRESS);
    }

    authRedirectInProgress = true;

    try {
      await redirect();
    } catch (redirectError) {
      const errorCode = (redirectError as { errorCode?: string }).errorCode;
      if (errorCode !== 'interaction_in_progress') {
        authRedirectInProgress = false;
        throw redirectError;
      }
    }

    throw new Error(AUTH_REDIRECT_IN_PROGRESS);
  };

  if (!activeAccount && accounts.length === 0) {
    // No account in cache: start interactive auth once.
    const { accessScope } = getRuntimeAuthConfig();
    return startAuthRedirect(() =>
      msalInstance.loginRedirect({ scopes: [accessScope] })
    );
  }
  const { accessScope } = getRuntimeAuthConfig();
  const request = {
    scopes: [accessScope],
    account: activeAccount || accounts[0],
  };

  try {
    const authResult = await msalInstance.acquireTokenSilent(request);

    return authResult.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // refresh token/session is not usable anymore; force interactive flow once.
      return startAuthRedirect(() =>
        msalInstance.acquireTokenRedirect(request)
      );
    }

    throw error;
  }
};

export { request };
